import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { Item } from '../../models/items.model';
import { MarketHistoriesResponse, MarketResponse } from '../../models/ao-data-interfaces';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { getLocationColorByIndex } from '../../constants/locations.constant';
import { Quality } from '../../models/quality.model';
import { getAllQualities } from '../../constants/qualities.constant';
import { PriceType } from '../../models/market.model';
import { PRICE_TYPES } from '../../constants/market.constant';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { RoundBadgeComponent } from './round-badge/round-badge.component';
import { SafeHtml } from '@angular/platform-browser';
import { SparkLineChartComponent } from '../spark-line-chart/spark-line-chart.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CopyTextButtonComponent } from '../copy-text-button/copy-text-button.component';

@Component({
  selector: 'app-item-price-card',
  imports: [
    CommonModule,
    SmallNumberPipe,
    RouterModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatButtonModule,
    MatTooltipModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    RoundBadgeComponent,
    SparkLineChartComponent,
    CopyTextButtonComponent,
  ],
  templateUrl: './item-price-card.component.html',
  styleUrls: ['./item-price-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemPriceCardComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) item: Item | null = null;
  @Input({ required: true }) prices: MarketResponse[] | null = null;
  @Input() histories: MarketHistoriesResponse[] | null = null;
  @Input() numberOfDaysAverage!: number;
  @Input({ required: true }) imageUrl: string | null = null;
  @Input() footerText: SafeHtml = '';
  @Input() showQualityLegend: boolean = false;
  @Input() priceType: PriceType = PRICE_TYPES.sell;
  @Input() showChart: boolean = true;

  qualities: Quality[] = getAllQualities();
  hoursDifferences: number[] = [];
  PRICE_TYPES = PRICE_TYPES; // Expose PRICE_TYPES to the template
  selectedMarketResponse?: MarketResponse = undefined;

  constructor(public aoDataGetterService: AoDataGetterService) {}

  getPrice(price: MarketResponse): number {
    switch (this.priceType) {
      case PRICE_TYPES.sell:
        return price.sellPriceMin;
      case PRICE_TYPES.buy:
        return price.buyPriceMax;
      case PRICE_TYPES.average:
        if (this.histories) {
          return (
            this.aoDataGetterService.getAveragePriceAndAmount(price.item, price.city, this.histories, price.qualityLevel, this.numberOfDaysAverage)
              .averagePrice ?? 0
          );
        } else return 0;
      default:
        return 0;
    }
  }

  // Lifecycle hook method triggered when an input property changes
  ngOnChanges(changes: SimpleChanges): void {
    // Check if the 'prices' input property has changed and if it has a value
    if (changes['prices'] && changes['prices'].currentValue !== changes['prices'].previousValue && this.prices) {
      this.computeHoursDifferences();
    }
  }

  ngOnDestroy(): void {
    this.hoursDifferences = [];
    this.qualities = [];
    this.item = null;
    this.prices = null;
    this.histories = null;
  }

  // Method to compute the hours differences for each price object
  computeHoursDifferences(): void {
    let result: number[];

    switch (this.priceType) {
      case PRICE_TYPES.sell:
        result = this.prices?.map((price) => this.getHoursDifference(price.sellPriceMinDate)) ?? [];
        break;
      case PRICE_TYPES.buy:
        result = this.prices?.map((price) => this.getHoursDifference(price.buyPriceMaxDate)) ?? [];
        break;
      case PRICE_TYPES.average:
        result = [];
        break;
      default:
        result = [];
        break;
    }
    this.hoursDifferences = result;
  }

  // Helper method to get the color associated with a city index
  getColorByIndex(index: number): string {
    return getLocationColorByIndex(index);
  }

  // Helper method to compute the hours difference between now and the given date
  getHoursDifference(priceDate: Date): number {
    const differenceInMilliseconds = new Date().getTime() - priceDate.getTime();
    const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
    return differenceInHours;
  }

  // Helper method to get the badge color based on the age of the price data
  getBadgeColor(priceAge: number): string {
    // Define intervals and associated colors
    if (priceAge <= 1) {
      return '#008000'; // Dark Green for age 0-1
    } else if (priceAge <= 3) {
      return '#b7b72c'; // Dark Yellow for age 1-3
    } else if (priceAge <= 6) {
      return '#AD8B00'; // Darker Gold for age 3-6
    } else if (priceAge <= 10) {
      return '#FFA500'; // Dark Orange for age 6-10
    } else if (priceAge <= 20) {
      return '#FF4500'; // Red-Orange for age 10-20
    } else {
      return '#8B0000'; // Dark Red for age > 20
    }
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  HandleChipClicked(price: MarketResponse) {
    if (this.selectedMarketResponse === price) {
      this.selectedMarketResponse = undefined;
    } else {
      this.selectedMarketResponse = price;
    }
  }
}
